import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TenderForm } from "@/components/bim/TenderForm";
import { TenderDetail } from "@/components/bim/TenderDetail";
import { Boxes, Loader2, Plus } from "lucide-react";

const AUTHORITY = ["public_authority", "collective_executive", "admin"];
const GRADE = { A: "#00FF7F", B: "#84cc16", C: "#F59E0B", D: "#F97316", E: "#EF4444" };

export default function Tenders() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => axios.get(`${API}/bim/tenders`).then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const open = useCallback((id) => axios.get(`${API}/bim/tenders/${id}`).then((r) => setSel(r.data)).catch(() => {}), []);
  useEffect(() => { if (list?.length && !sel) { const want = new URLSearchParams(window.location.search).get("tender"); open(want || list[0].id); } }, [list, sel, open]);

  const canCreate = user && AUTHORITY.includes(user.role);

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 text-[#F59E0B] text-xs font-mono uppercase tracking-widest"><Boxes className="h-4 w-4" /> 9D BIM · public tenders</span>
            <h1 className="font-display font-bold text-3xl md:text-5xl mt-2 tracking-tight" data-testid="tenders-title">Tender feasibility, auditable by anyone</h1>
            <p className="text-slate-400 mt-3 max-w-3xl">Massing, schedule, cost & valuation, carbon, lifecycle, seismic/AFAD safety and social value — scored, narrated, hash-anchored, and open to dispute. A blockchain spine for AFAD-grade, carbon-neutral public building.</p>
          </div>
          {canCreate && (
            <button data-testid="new-tender-btn" onClick={() => setShowForm(!showForm)} className="qs-cta bg-[#F59E0B] text-[#050A15] font-semibold px-4 py-2.5 text-sm flex items-center gap-2">
              <Plus className="h-4 w-4" /> {showForm ? "Close" : "New tender"}
            </button>
          )}
        </div>

        {showForm && canCreate && <div className="mt-6"><TenderForm onCreated={(t) => { setShowForm(false); setSel({ ...t, claims: [] }); load(); }} /></div>}

        <div className="grid lg:grid-cols-5 gap-6 mt-8">
          <div className="lg:col-span-2 qs-panel overflow-hidden" data-testid="tender-list">
            {!list ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#F59E0B]" /></div>
              : list.map((t) => (
                <button key={t.id} data-testid={`tender-row-${t.id}`} onClick={() => open(t.id)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-center gap-3 hover:bg-white/[0.03] ${sel?.id === t.id ? "bg-[#F59E0B]/10" : ""}`}>
                  <span className="font-mono font-bold text-xl w-14" style={{ color: GRADE[t.grade] }}>{t.feasibility_score}</span>
                  <span className="flex-1 min-w-0"><div className="font-display font-semibold truncate">{t.title}</div><div className="text-xs text-slate-500">{t.project_type.replace("_", " ")} · {t.city} · {t.recommendation}{t.material_gate?.assessed && <span className={t.material_gate.passed ? "text-[#00FF7F]" : "text-[#EF4444]"} data-testid={`tender-row-gate-${t.id}`}> · gate {t.material_gate.passed ? "ok" : "FAILED"}</span>}</div></span>
                </button>
              ))}
            {list && !list.length && <p className="text-sm text-slate-500 p-4">No tenders published yet.</p>}
          </div>
          <div className="lg:col-span-3">{sel && <TenderDetail t={sel} onChanged={() => open(sel.id)} onOpen={open} onRescored={(nt) => { setSel(nt); load(); open(nt.id); }} />}</div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
